import { useEffect, useRef, useState } from 'react'
import type { Step } from 'react-joyride'
import {
  filterAvailableSteps,
  hasRenderedTargets,
} from './filter-available-steps'
import { resolveStepElement } from './scroll-target-into-horizontal-view'

export const SETTLE_POLL_INTERVAL = 250
export const SETTLE_MAX_ATTEMPTS = 20

export function useSettledSteps(
  steps: Step[],
  tourId: string,
  initialStepIndex?: number,
): Step[] | null {
  const [settled, setSettled] = useState<{
    tourId: string
    steps: Step[]
  } | null>(null)
  const stepsRef = useRef(steps)
  stepsRef.current = steps

  useEffect(() => {
    setSettled(null)
    let previousCount = -1
    let attempts = 0

    const interval = setInterval(() => {
      attempts += 1

      const available = filterAvailableSteps(stepsRef.current)
      const initialStep = available[initialStepIndex ?? 0]
      const isInitialTargetReady =
        initialStepIndex === undefined ||
        Boolean(initialStep && resolveStepElement(initialStep.target))
      const hasTargets =
        hasRenderedTargets(available) ||
        (initialStepIndex !== undefined && initialStep?.target === 'body')
      const hasStoppedGrowing = hasTargets && available.length === previousCount

      const maxAttempts = Math.max(
        SETTLE_MAX_ATTEMPTS,
        Math.ceil((initialStep?.targetWaitTimeout ?? 0) / SETTLE_POLL_INTERVAL),
      )
      if (
        (hasStoppedGrowing && isInitialTargetReady) ||
        attempts >= maxAttempts
      ) {
        clearInterval(interval)
        if (hasTargets) setSettled({ tourId, steps: available })
        return
      }

      previousCount = available.length
    }, SETTLE_POLL_INTERVAL)

    return () => clearInterval(interval)
  }, [tourId, initialStepIndex])

  return settled?.tourId === tourId ? settled.steps : null
}
